import { Component, computed, inject, Input, OnInit, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { OperationRequirement, OperationRequirementService, StagingSummaryEntry } from "../../../_services/operation-requirement.service";
import { ShippingConfig, ShippingConfigContext, ShippingConfigFormData, ShippingConfigType } from "../../../_models/shipping-config.interface";
import { ShippingConfigPayload, ShippingConfigService } from "../../../_services/shipping-config.service";
import { ShippingConfigFormComponent } from "../../_shared/shipping-config-form/shipping-config-form.component";
import { ArticleService } from "../../../_services";
import { ArticleTemplate } from "../../../_models";
import { StagingConfigService } from "../../../_services/staging-config.service";

// Versandtypen, die Staging-Artikel mit abdecken (Kunden-Rueckversand NICHT).
const COVERING_TYPES: ShippingConfigType[] = ['technician_shipping', 'store_shipping', 'pickpoint'];

type ActionType = "STAGING" | "VERSAND";

/**
 * Phase 3 (Multi-Config + Mengen-Split): Matrix-Darstellung des Artikelbedarfs
 * auf der Ticketseite. Zeilen = OperationRequirements (gruppiert VERSAND /
 * STAGING), Spalten = aktive ShippingConfigs (+ synthetische Spalte
 * "Staging-Standardversand", solange keine deckende Config existiert).
 *
 * Zellen = Mengen-Split (shipping_config_requirements); Stepper +/- verteilen
 * die Bedarfsmenge ueber die Configs, der unverteilte Rest steht als "offen N"
 * am Zeilenanfang. Bereits versandte/vorbereitete Mengen sind (konservativ auf
 * Zeilenebene) gegen Reduzierung gesperrt.
 */
@Component({
  selector: "app-operation-shipping-matrix",
  standalone: true,
  imports: [FormsModule, RouterLink, ShippingConfigFormComponent],
  templateUrl: "./operation-shipping-matrix.component.html",
})
export class OperationShippingMatrixComponent implements OnInit {
  @Input({ required: true }) operationId!: number;
  // Durchgereicht ans Config-Form (Editor im Spalten-Header).
  @Input() projectId?: number;
  @Input() ticketStoreId?: number;
  @Input() ticketLat?: number;
  @Input() ticketLng?: number;
  @Input() referenceDate?: string;
  @Input() operationTechnicianId?: number;

  private reqService = inject(OperationRequirementService);
  private configService = inject(ShippingConfigService);
  private articleService = inject(ArticleService);
  private stagingConfigService = inject(StagingConfigService);

  protected requirements = signal<OperationRequirement[]>([]);
  protected configs = signal<ShippingConfig[]>([]);
  protected isSaving = signal<boolean>(false);
  protected isLoading = signal<boolean>(true);

  // Lokaler Split-State: cells[configId][requirementId] = quantity.
  protected cells = signal<Map<number, Map<number, number>>>(new Map());

  private saveTimers = new Map<number, ReturnType<typeof setTimeout>>();

  // Staging-Versand-Status je Anforderung (operation_requirement_id → Eintrag).
  // Wird genutzt, um in der Standard-Staging-Spalte (ohne deckende Config) die
  // bereits versandte/verzoegerte Menge je Artikel zu zeigen.
  protected stagingSummary = signal<StagingSummaryEntry[]>([]);
  protected stagingSummaryById = computed(() => {
    const map = new Map<number, StagingSummaryEntry>();
    for (const entry of this.stagingSummary()) map.set(entry.operation_requirement_id, entry);
    return map;
  });

  async ngOnInit() {
    await Promise.all([this.reload(), this.loadTemplates()]);
  }

  async reload() {
    this.isLoading.set(true);
    const [reqs, configs, stagingSummary] = await Promise.all([
      this.reqService.loadRequirements(this.operationId),
      this.configService.getMatrixConfigs(this.operationId),
      this.reqService.loadStagingSummary(this.operationId),
    ]);
    this.requirements.set(reqs);
    this.configs.set(configs);
    this.stagingSummary.set(stagingSummary);
    this.initCellsFrom(configs, reqs);
    this.isLoading.set(false);
  }

  // Initialer Zell-Stand = max(geplanter Split, bereits committed). Aktive
  // Configs sind voll editierbar; archivierte (abgeschlossene) Configs sind nur
  // fuer STAGING-Zuordnungen editierbar (deckender Typ) — ihre VERSAND-Zellen
  // bleiben read-only und stehen nicht im cells-State.
  private initCellsFrom(configs: ShippingConfig[], reqs: OperationRequirement[]) {
    const map = new Map<number, Map<number, number>>();
    for (const c of configs) {
      const splitByReq = new Map<number, number>();
      for (const s of c.requirement_split ?? []) splitByReq.set(s.operation_requirement_id, s.quantity);
      const inner = new Map<number, number>();
      for (const r of reqs) {
        // Staging nur auf deckenden Configs zuweisbar.
        if (!this.canAssign(c, r)) continue;
        // Ein bereits versandter VERSAND-Kanal ist read-only (wird als ✓ gerendert,
        // nicht als Stepper) und gehoert daher nicht in den editierbaren State.
        // Staging-Zellen bleiben (auch bei versandtem Staging) als ueber cellLocked
        // gesperrte Stepper im State — so zaehlen sie weiter in assignedTotal.
        if (r.action_type === 'VERSAND' && c.versand_shipped) continue;
        const qty = Math.max(splitByReq.get(r.id) ?? 0, this.cellLocked(c, r));
        if (qty > 0) inner.set(r.id, qty);
      }
      map.set(c.id, inner);
    }
    this.cells.set(map);
  }

  // Committe (versandte/zurueckgehaltene) Mengen dieser Config fuer das
  // Requirement — keyed by operation_requirement_id (VERSAND + STAGING).
  private committedFor(config: ShippingConfig, req: OperationRequirement): { shipped: number; held: number } {
    const e = (config.requirement_committed ?? []).find((x) => x.operation_requirement_id === req.id);
    return { shipped: e?.shipped ?? 0, held: e?.held ?? 0 };
  }

  protected committedShipped(config: ShippingConfig, req: OperationRequirement): number {
    return this.committedFor(config, req).shipped;
  }

  protected committedHeld(config: ShippingConfig, req: OperationRequirement): number {
    return this.committedFor(config, req).held;
  }

  // Gesperrte Mindestmenge dieser Zelle (versandt + zurueckgehalten).
  protected cellLocked(config: ShippingConfig, req: OperationRequirement): number {
    const c = this.committedFor(config, req);
    return c.shipped + c.held;
  }

  // --- Zeilen-Gruppen ---
  protected versandRows = computed(() => this.requirements().filter((r) => r.action_type === 'VERSAND'));
  protected stagingRows = computed(() => this.requirements().filter((r) => r.action_type === 'STAGING'));

  // --- deckende Configs + Standard-Staging-Spalte ---
  // Deckende Configs (aktiv ODER archiviert) koennen Staging-Artikel aufnehmen.
  protected coveringConfigs = computed(() =>
    this.configs().filter((c) => COVERING_TYPES.includes(c.shipping_type)),
  );
  // Die "Standard"-Spalte ist der Umschalt-Gegenpart zu den Config-Spalten:
  // sie zeigt den noch NICHT einer Config zugewiesenen Staging-Rest (poolFor).
  // Sobald Staging-Zeilen existieren, ist sie sichtbar — der Lagerist verschiebt
  // ueber die Config-Stepper zwischen "an Config" und "Standard ohne Config".
  protected showStandardStaging = computed(() => this.stagingRows().length > 0);

  // --- Zell-/Zeilen-Helfer ---
  protected cellQty(configId: number, reqId: number): number {
    return this.cells().get(configId)?.get(reqId) ?? 0;
  }

  protected assignedTotal(reqId: number): number {
    let sum = 0;
    for (const inner of this.cells().values()) sum += inner.get(reqId) ?? 0;
    return sum;
  }

  // Bereits ueber Configs mit versandtem VERSAND-Kanal verschickte Menge dieses
  // VERSAND-Requirements — verlaesst den offenen Pool (die Zellen sind read-only
  // und liegen NICHT im editierbaren State, daher hier separat abziehen). Gilt
  // sowohl fuer vollstaendig archivierte als auch fuer teilweise versandte
  // Configs (VERSAND raus, Staging noch offen). Nur VERSAND: Staging-Zuordnungen
  // bleiben als editierbare (gesperrte) Zellen im State → ueber assignedTotal.
  protected versandShippedConsumed(req: OperationRequirement): number {
    if (req.action_type !== 'VERSAND') return 0;
    let sum = 0;
    for (const c of this.configs()) {
      if (!c.versand_shipped) continue;
      const cm = this.committedFor(c, req);
      sum += cm.shipped + cm.held;
    }
    return sum;
  }

  protected poolFor(req: OperationRequirement): number {
    return Math.max(0, req.quantity - this.assignedTotal(req.id) - this.versandShippedConsumed(req));
  }

  // Nur noch INFORMATIV fuer die Zeilen-Anzeige: gesamte ueber Configs mit
  // versandtem VERSAND-Kanal bereits verschickte VERSAND-Menge. Die eigentliche
  // Sperre laeuft per Zelle ueber cellLocked (canDecrement); der versandte Teil
  // ist zusaetzlich ueber versandShippedConsumed/poolFor aus dem Pool reserviert.
  protected lockedFloor(req: OperationRequirement): number {
    return req.action_type === 'VERSAND' ? (req.already_shipped_quantity ?? 0) : 0;
  }

  // Zeile vollstaendig erledigt: VERSAND ueber already_shipped_quantity,
  // STAGING ueber versandt+vorbereitet aus dem Staging-Summary. Markiert die
  // Artikel-Zeile als abgeschlossen (z.B. komplett per PickPoint versandt).
  protected requirementFullyShipped(req: OperationRequirement): boolean {
    if (req.quantity <= 0) return false;
    if (req.action_type === 'VERSAND') return (req.already_shipped_quantity ?? 0) >= req.quantity;
    const s = this.stagingSummaryById().get(req.id);
    return !!s && s.shipped + s.delayed >= req.quantity;
  }

  // Staging-Artikel duerfen nur deckenden Configs (Techniker/Filiale/PickPoint)
  // zugeordnet werden, Versandartikel jeder Config.
  protected canAssign(config: ShippingConfig, req: OperationRequirement): boolean {
    return req.action_type === 'VERSAND' || COVERING_TYPES.includes(config.shipping_type);
  }

  protected canIncrement(config: ShippingConfig, req: OperationRequirement): boolean {
    return this.canAssign(config, req) && this.poolFor(req) > 0;
  }

  protected canDecrement(config: ShippingConfig, req: OperationRequirement): boolean {
    if (this.cellQty(config.id, req.id) <= 0) return false;
    // Per-Config-Lock (VERSAND wie STAGING): die Zelle darf nicht unter die ueber
    // DIESE Config bereits committe (versandt + vorbereitet/verzoegert) Menge
    // fallen. Schon ueber archivierte Configs versandte VERSAND-Mengen liegen
    // separat im Pool (archivedConsumed) und blockieren daher nicht mehr
    // faelschlich die aktive Nachversand-Config.
    return this.cellQty(config.id, req.id) - 1 >= this.cellLocked(config, req);
  }

  protected increment(config: ShippingConfig, req: OperationRequirement) {
    if (!this.canIncrement(config, req)) return;
    this.setCell(config.id, req.id, this.cellQty(config.id, req.id) + 1);
  }

  protected decrement(config: ShippingConfig, req: OperationRequirement) {
    if (!this.canDecrement(config, req)) return;
    this.setCell(config.id, req.id, this.cellQty(config.id, req.id) - 1);
  }

  private setCell(configId: number, reqId: number, qty: number) {
    const map = new Map(this.cells());
    const inner = new Map(map.get(configId) ?? new Map<number, number>());
    if (qty <= 0) inner.delete(reqId);
    else inner.set(reqId, qty);
    map.set(configId, inner);
    this.cells.set(map);
    this.scheduleSave(configId);
  }

  private scheduleSave(configId: number) {
    const existing = this.saveTimers.get(configId);
    if (existing) clearTimeout(existing);
    this.saveTimers.set(configId, setTimeout(() => void this.persist(configId), 500));
  }

  private async persist(configId: number) {
    const inner = this.cells().get(configId) ?? new Map<number, number>();
    const requirements = Array.from(inner.entries())
      .filter(([, q]) => q > 0)
      .map(([operation_requirement_id, quantity]) => ({ operation_requirement_id, quantity }));
    this.isSaving.set(true);
    try {
      await this.configService.saveConfigSplit(this.operationId, configId, requirements);
    } catch {
      // Bei Konflikt (z.B. Ueberbuchung durch parallele Aenderung) den Server-
      // Stand uebernehmen.
      await this.reload();
    } finally {
      this.isSaving.set(false);
    }
  }

  // --- Config-Spalten verwalten ---
  // "+ Versand": NICHT sofort anlegen, sondern den Editor oeffnen. Die Config
  // wird erst beim Speichern im Popup angelegt (mit Auftragstechniker vorbelegt —
  // siehe shipping-config-form, Default fuer neue Configs).
  protected async addConfig() {
    this.editingConfig.set(null);
    this.creatingConfig.set(true);
    this.editorContext.set(null);
    try {
      this.editorContext.set(await this.configService.getContext(this.operationId));
    } catch {
      this.editorContext.set({ customer_has_return_address: false });
    }
  }

  // Config mit durchgefuehrter/vorbereiteter Sendung (Lieferscheine oder
  // committe Mengen) darf nicht geloescht werden — der Backend-destroy lehnt
  // das ebenfalls ab (422).
  protected configHasCommitted(c: ShippingConfig): boolean {
    if ((c.delivery_notes?.length ?? 0) > 0) return true;
    return (c.requirement_committed ?? []).some((x) => x.shipped + x.held > 0);
  }

  protected canDeleteConfig(c: ShippingConfig): boolean {
    return !c.archived && !this.configHasCommitted(c);
  }

  protected async removeConfig(configId: number) {
    if (!confirm('Diese Versand-Config entfernen?')) return;
    try {
      await this.configService.deleteConfigById(this.operationId, configId);
    } catch {
      // Backend lehnt das Loeschen ab (z.B. bereits Sendungen) — Stand neu laden.
    }
    await this.reload();
  }

  // --- Lieferschein-Popup (archivierte Spalte) ---
  protected dnPopupConfig = signal<ShippingConfig | null>(null);

  protected openDnPopup(config: ShippingConfig) {
    this.dnPopupConfig.set(config);
  }

  protected closeDnPopup() {
    this.dnPopupConfig.set(null);
  }

  // --- Config-Editor (Popup) ---
  protected editingConfig = signal<ShippingConfig | null>(null);
  // true = Editor im "Neu anlegen"-Modus (Config wird erst beim Speichern erzeugt).
  protected creatingConfig = signal<boolean>(false);
  protected editorContext = signal<ShippingConfigContext | null>(null);

  protected async openEditor(config: ShippingConfig) {
    this.creatingConfig.set(false);
    this.editingConfig.set(config);
    this.editorContext.set(null);
    try {
      this.editorContext.set(await this.configService.getContext(this.operationId));
    } catch {
      this.editorContext.set({ customer_has_return_address: false });
    }
  }

  protected closeEditor() {
    this.editingConfig.set(null);
    this.creatingConfig.set(false);
  }

  protected async onEditorSave(data: ShippingConfigFormData) {
    const payload = data as unknown as ShippingConfigPayload;
    if (this.creatingConfig()) {
      // Neue (zusaetzliche) Config erst jetzt anlegen.
      await this.configService.createAdditionalConfig(this.operationId, payload);
    } else {
      const cfg = this.editingConfig();
      if (!cfg) return;
      await this.configService.updateConfigById(this.operationId, cfg.id, payload);
    }
    this.closeEditor();
    await this.reload();
  }

  // --- Anzeige-Helfer ---
  protected configLabel(c: ShippingConfig): string {
    return c.shipping_type_label || c.shipping_type;
  }

  protected reqLabel(r: OperationRequirement): string {
    return r.article_template_description || r.free_text || '—';
  }

  // Summe der ueber Configs versandten / zurueckgehaltenen Staging-Menge dieses
  // Requirements (ueber alle Spalten) — Basis, um den Standard-Anteil sauber vom
  // Config-Anteil zu trennen.
  private sumCommittedShipped(req: OperationRequirement): number {
    return this.configs().reduce((s, c) => s + this.committedShipped(c, req), 0);
  }

  private sumCommittedHeld(req: OperationRequirement): number {
    return this.configs().reduce((s, c) => s + this.committedHeld(c, req), 0);
  }

  // In der Standard-Staging-Spalte (ohne deckende Config) versandte/verzoegerte
  // Mengen — NUR der Anteil, der NICHT einer Config zugeordnet ist. Das
  // Staging-Summary zaehlt alle Records; die config-zugeordneten Mengen werden
  // bereits in der jeweiligen Config-Spalte angezeigt, daher hier abziehen
  // (sonst erscheint z.B. ein verzoegerter PickPoint-Record doppelt).
  protected standardStagingShipped(req: OperationRequirement): number {
    const total = this.stagingSummaryById().get(req.id)?.shipped ?? 0;
    return Math.max(0, total - this.sumCommittedShipped(req));
  }

  protected standardStagingDelayed(req: OperationRequirement): number {
    const total = this.stagingSummaryById().get(req.id)?.delayed ?? 0;
    return Math.max(0, total - this.sumCommittedHeld(req));
  }

  // --- Datum-/Timing-Infos fuer die Section-Header + verzoegertes Staging ----
  private formatShortDate(iso?: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  // Geplanter VERSAND-Termin dieser Config (config.shipping_date) — pro Config im
  // Spalten-Header. null = am Auftragsdatum (kein expliziter Termin).
  protected configVersandDate(config: ShippingConfig): string | null {
    return this.formatShortDate(config.shipping_date);
  }

  // Verzoegerter Staging-Versand dieser Config (staging_ship_ready_at) — pro
  // Config im Spalten-Header. null = sofort versandfertig (keine Verzoegerung).
  protected configStagingDelayDate(config: ShippingConfig): string | null {
    return this.formatShortDate(config.staging_ship_ready_at);
  }

  // ===========================================================================
  // Artikel-CRUD (Anlegen/Bearbeiten/Loeschen + Legacy-Parsing). Frueher in der
  // separaten operation-requirements-Komponente; seit Phase 3 produktiv hier.
  // ===========================================================================

  protected showModal = signal<boolean>(false);
  protected editingRequirement = signal<OperationRequirement | null>(null);
  protected searchResults = signal<ArticleTemplate[]>([]);
  protected selectedTemplate = signal<ArticleTemplate | null>(null);
  protected parsingInProgress = signal<boolean>(false);

  // Artikelvorlagen: alle (Versand-Suche) + nur Staging-Vorlagen des Projekts.
  protected allTemplates = signal<ArticleTemplate[]>([]);
  protected stagingTemplates = signal<{ id: number; description: string }[]>([]);

  // Formular-Felder. formActionType wird durch den jeweiligen Section-Add-Button
  // gesetzt — kein Type-Switch im Modal.
  protected formActionType: ActionType = "VERSAND";
  protected formQuantity = 1;
  protected formSearchText = "";
  protected formStagingTemplateId: number | null = null;

  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  private async loadTemplates() {
    // Alle Artikelvorlagen laden (fuer Versand-Suche)
    const all = await firstValueFrom(this.articleService.loadArticleTemplates());
    this.allTemplates.set(all || []);

    // Staging-Artikelvorlagen aus den Staging-Configs des Projekts laden
    if (this.projectId) {
      await this.stagingConfigService.loadConfigs(this.projectId);
      const configs = this.stagingConfigService.configs();
      const templates = configs
        .filter((c) => c.enabled && c.article_template_id && c.article_template_description)
        .map((c) => ({ id: c.article_template_id!, description: c.article_template_description! }));
      const unique = [...new Map(templates.map((t) => [t.id, t])).values()];
      this.stagingTemplates.set(unique);
    }
  }

  // --- Modal oeffnen/schliessen ---
  protected openAdd(actionType: ActionType) {
    this.resetForm();
    this.formActionType = actionType;
    this.editingRequirement.set(null);
    this.showModal.set(true);
  }

  protected openEdit(req: OperationRequirement) {
    this.resetForm();
    this.editingRequirement.set(req);
    this.formActionType = req.action_type;
    this.formQuantity = req.quantity;

    if (req.action_type === "STAGING" && req.article_template_id) {
      this.formStagingTemplateId = req.article_template_id;
      this.selectedTemplate.set({ id: req.article_template_id, description: req.article_template_description || "" } as ArticleTemplate);
    } else if (req.article_template_id) {
      this.selectedTemplate.set({ id: req.article_template_id, description: req.article_template_description || "" } as ArticleTemplate);
      this.formSearchText = req.article_template_description || "";
    } else {
      this.formSearchText = req.free_text || "";
    }

    this.showModal.set(true);
  }

  protected closeModal() {
    this.showModal.set(false);
    this.editingRequirement.set(null);
    this.resetForm();
  }

  protected onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) this.closeModal();
  }

  // --- Formular-Logik ---
  protected onStagingTemplateSelect(templateId: number | null) {
    if (!templateId) {
      this.selectedTemplate.set(null);
      return;
    }
    const tpl = this.stagingTemplates().find((t) => t.id === templateId);
    if (tpl) this.selectedTemplate.set({ id: tpl.id, description: tpl.description } as ArticleTemplate);
  }

  protected onSearchInput() {
    this.selectedTemplate.set(null);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);

    if (this.formSearchText.trim().length < 2) {
      this.searchResults.set([]);
      return;
    }
    this.searchDebounce = setTimeout(() => this.performSearch(), 300);
  }

  private performSearch() {
    const query = this.formSearchText.toLowerCase();
    const results = this.allTemplates().filter(
      (t) => t.description?.toLowerCase().includes(query) || t.part_nr?.toLowerCase().includes(query),
    );
    this.searchResults.set(results.slice(0, 15));
  }

  protected selectTemplate(template: ArticleTemplate) {
    this.selectedTemplate.set(template);
    this.formSearchText = template.description;
    this.searchResults.set([]);
  }

  protected onSearchBlur() {
    this.searchResults.set([]);
  }

  protected formMinQuantity = computed<number>(() => {
    const editing = this.editingRequirement();
    if (!editing || editing.action_type !== "VERSAND") return 1;
    return Math.max(1, editing.already_shipped_quantity || 0);
  });

  protected canSave(): boolean {
    if (this.formQuantity < this.formMinQuantity()) return false;
    if (this.formActionType === "STAGING") return this.selectedTemplate() !== null;
    return this.selectedTemplate() !== null || this.formSearchText.trim().length > 0;
  }

  protected async saveRequirement() {
    const template = this.selectedTemplate();
    const data: Partial<OperationRequirement> = {
      action_type: this.formActionType,
      quantity: this.formQuantity,
    };

    if (template) {
      data.article_template_id = template.id;
      data.free_text = null as any;
    } else {
      data.free_text = this.formSearchText.trim();
      data.article_template_id = null as any;
    }

    const editing = this.editingRequirement();
    if (editing) {
      const updated = await this.reqService.updateRequirement(editing.id, data);
      if (updated) {
        this.closeModal();
        // Backend kann bei Versand-Mengen-Aenderung eine Auto-Config anlegen —
        // reload() laedt Requirements UND Configs neu.
        await this.reload();
      }
    } else {
      const result = await this.reqService.createRequirement(this.operationId, data);
      if (result) {
        this.closeModal();
        await this.reload();
      }
    }
  }

  // Versand-Artikel sind nur loeschbar, solange nichts versandt wurde; Staging-
  // Artikel jederzeit (Backend erzwingt die Detailregeln).
  protected canDelete(req: OperationRequirement): boolean {
    return req.action_type === "STAGING" || (req.already_shipped_quantity ?? 0) === 0;
  }

  protected async deleteRequirement(req: OperationRequirement) {
    const label = req.article_template_description || req.free_text || "Unbekannt";
    if (!confirm(`"${req.quantity}x ${label}" wirklich löschen?`)) return;
    const success = await this.reqService.deleteRequirement(req.id);
    if (success) await this.reload();
  }

  // Legt Versand-Requirements aus dem Legacy-Description-Parsing an. Nur erlaubt,
  // solange noch keine Versand-Requirements existieren (sonst Doppelungen).
  protected async parseFromDescription() {
    if (this.versandRows().length > 0) return;
    if (!confirm("Versand-Artikel aus der Auftragsbeschreibung erzeugen?")) return;

    this.parsingInProgress.set(true);
    try {
      const result = await this.reqService.parseFromDescription(this.operationId);
      if ("error" in result) {
        alert(result.error);
        return;
      }
      await this.reload();
    } finally {
      this.parsingInProgress.set(false);
    }
  }

  private resetForm() {
    this.formActionType = "VERSAND";
    this.formQuantity = 1;
    this.formSearchText = "";
    this.selectedTemplate.set(null);
    this.formStagingTemplateId = null;
    this.searchResults.set([]);
  }
}
